import { useGetExamRoomByIdQuery } from '@exam-center/graphql';
import { Loading } from '@shared/components';
import { type PropsWithChildren } from '@shared/utils';
import { createContext, useContext, useMemo } from 'react';

import { type RoomDetails } from './types';

interface ExamRoomDetailsContextValue {
  roomDetails: RoomDetails;
  examCenterCode: string;
}

export interface ExamLocationDetailsProviderProps extends PropsWithChildren {
  roomId: string;
  examCenterCode: string;
}

const ExamLocationDetailsContext = createContext<ExamRoomDetailsContextValue | undefined>(
  undefined,
);

export function useExamLocationDetails() {
  const context = useContext(ExamLocationDetailsContext);
  if (!context) {
    throw new Error(
      'useExamLocationDetailsContext must be used within an ExamLocationDetailsProvider',
    );
  }
  return context;
}

export default function ExamLocationDetailsProvider(
  props: Readonly<ExamLocationDetailsProviderProps>,
) {
  const { children, examCenterCode, roomId } = props;

  const { data: getRoomByIdData, loading: isLoading } = useGetExamRoomByIdQuery({
    variables: {
      getExamRoomByIdInput: {
        id: roomId,
      },
    },
  });

  const roomDetails = getRoomByIdData?.ecGetExamRoomById.examRoom;

  const contextValue: ExamRoomDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      roomDetails: roomDetails!,
      examCenterCode,
    };
  }, [examCenterCode, roomDetails]);

  if (isLoading) {
    return <Loading height='100%' />;
  }

  return (
    <ExamLocationDetailsContext.Provider value={contextValue}>
      {children}
    </ExamLocationDetailsContext.Provider>
  );
}
